import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";

/** Inline editable <select> — saves the chosen option immediately via PATCH.
 *  Used for fields with managed dropdown options (bloom_level, question_type,
 *  difficulty). Falls back to a read-only span when `editable` is false.
 */
export default function InlineSelect({
  questionId,
  field,
  value,
  options = [], // [{id, value}] OR ["string", ...]
  editable = true,
  placeholder = "—",
  className = "",
  onSaved,
}) {
  const [busy, setBusy] = useState(false);

  const normalised = options.map((o) =>
    typeof o === "string" ? { id: o, value: o } : o
  );

  const change = async (next) => {
    if (busy) return;
    if ((next ?? "") === (value ?? "")) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/questions/${questionId}`, {
        [field]: next || "",
      });
      if (onSaved) onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!editable) {
    return (
      <span className={className} data-testid={`inline-readonly-${field}`}>
        {value || placeholder}
      </span>
    );
  }

  return (
    <div className="relative inline-block w-full">
      <select
        value={value || ""}
        onChange={(e) => change(e.target.value)}
        disabled={busy}
        data-testid={`inline-select-${field}`}
        className={`w-full appearance-none pl-1.5 pr-6 py-0.5 rounded-md border border-transparent hover:border-slate-200 hover:bg-slate-50 focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-500/15 outline-none transition disabled:opacity-50 ${className}`}
      >
        <option value="">{placeholder}</option>
        {/* Include the current value even if it's no longer in the option list */}
        {value && !normalised.find((o) => o.value === value) && (
          <option value={value}>{value} (legacy)</option>
        )}
        {normalised.map((o) => (
          <option key={o.id} value={o.value}>
            {o.value}
          </option>
        ))}
      </select>
      <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
    </div>
  );
}
